/**
 * ActiveSessionsList Component
 * Displays user's active sessions with ability to logout from specific devices
 */

"use client";

import React, { useEffect } from "react";
import useSessionManagement from "@/hooks/useSessionManagement";
import { UserSession } from "@/types/session";
import { formatDistanceToNow } from "date-fns";
import { ar } from "date-fns/locale";
import { Loader2, LogOut, AlertTriangle } from "lucide-react";

export const ActiveSessionsList = () => {
  const {
    activeSessions,
    sharingDetected,
    anomalies,
    loading,
    logoutFromDevice,
    fetchActiveSessions,
  } = useSessionManagement({
    autoCheck: false, // Manual control
  });

  useEffect(() => {
    // Fetch sessions on component mount
    fetchActiveSessions();
  }, [fetchActiveSessions]);

  const getDeviceIcon = (device: string | undefined) => {
    const deviceLower = (device || "").toLowerCase();
    if (
      deviceLower.includes("mobile") ||
      deviceLower.includes("iphone") ||
      deviceLower.includes("android")
    ) {
      return "📱";
    }
    if (deviceLower.includes("tablet") || deviceLower.includes("ipad")) {
      return "📱";
    }
    return "💻";
  };

  const getLocationFromIp = (ip: string | undefined) => {
    if (!ip) return "غير معروف";
    return ip;
  };

  const handleLogout = async (sessionId: number) => {
    if (window.confirm("هل تريد إنهاء هذه الجلسة؟")) {
      await logoutFromDevice(sessionId);
    }
  };

  return (
    <div className="w-full p-6">
      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          الجلسات النشطة
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          إدارة أجهزتك والحفاظ على أمان حسابك
        </p>
      </div>

      {/* Alert for Account Sharing */}
      {sharingDetected && anomalies.length > 0 && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-900 dark:text-red-100 mb-2">
                تحذير: نشاط غير عادي
              </h3>
              <ul className="space-y-1 text-sm text-red-800 dark:text-red-200">
                {anomalies.map((anomaly: string, index: number) => (
                  <li key={index} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 bg-red-600 dark:bg-red-400 rounded-full"></span>
                    {anomaly}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-400" />
        </div>
      )}

      {/* Sessions List */}
      {!loading && activeSessions.length > 0 && (
        <div className="space-y-4">
          {activeSessions.map((session: UserSession) => (
            <div
              key={session.id}
              className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                {/* Device Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-2xl">
                      {getDeviceIcon(session.device)}
                    </span>
                    <div>
                      <h3 className="font-semibold text-gray-900 dark:text-white">
                        {session.device || "جهاز"} -{" "}
                        {session.browser || "المتصفح"}
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {session.platform || "نظام التشغيل"}
                      </p>
                    </div>
                  </div>

                  {/* Session Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <div>
                      <span className="font-medium">عنوان IP:</span>{" "}
                      {getLocationFromIp(session.ip_address)}
                    </div>
                    <div>
                      <span className="font-medium">آخر نشاط:</span>{" "}
                      {formatDistanceToNow(new Date(session.last_activity), {
                        locale: ar,
                        addSuffix: true,
                      })}
                    </div>
                    <div>
                      <span className="font-medium">تاريخ الدخول:</span>{" "}
                      {new Date(session.created_at).toLocaleDateString("ar-EG")}
                    </div>
                    {session.is_current && (
                      <div>
                        <span className="inline-block bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 px-2 py-1 rounded text-xs font-medium">
                          الجلسة الحالية
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Logout Button */}
                {!session.is_current && (
                  <button
                    onClick={() => handleLogout(session.id)}
                    className="flex items-center gap-2 px-3 py-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-lg transition-colors text-sm font-medium"
                    type="button"
                  >
                    <LogOut className="w-4 h-4" />
                    إنهاء
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {!loading && activeSessions.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            لا توجد جلسات نشطة حالياً
          </p>
        </div>
      )}

      {/* Refresh Button */}
      <div className="mt-6 flex gap-3">
        <button
          onClick={() => fetchActiveSessions()}
          disabled={loading}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg transition-colors font-medium"
        >
          تحديث
        </button>
      </div>
    </div>
  );
};

export default ActiveSessionsList;
